import { fetchWithAuth } from '../utils/fetchWithAuth';

export interface MentionUser {
  accountId: string;
  displayName: string;
  avatarUrl?: string;
}

export interface Comment {
  id: string;
  text: string;
  author: string;
  created: string;
  images?: { url?: string; src?: string; filename?: string }[];
}

// Mirrors the `comments` block of a desk in backend/config/config.prod.ts; write implies read.
export interface CommentRights {
  read: string[];
  write: string[];
}

export interface DeskWithComments {
  comments?: CommentRights | null;
}

export type CommentScope = 'user' | 'admin';

const commentsPath = (issueKey: string, scope: CommentScope) =>
  `${scope === 'admin' ? 'admin/' : ''}issues/${encodeURIComponent(issueKey)}/comments`;

export const commentsApi = {
  fetchComments: async (issueKey: string, scope: CommentScope = 'user'): Promise<Comment[]> => {
    const response = await fetchWithAuth(commentsPath(issueKey, scope));
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to fetch comments');
    }

    return data.data.comments || [];
  },

  addComment: async (issueKey: string, text: string, mentions?: MentionUser[], scope: CommentScope = 'user') => {
    const response = await fetchWithAuth(commentsPath(issueKey, scope), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, mentions: mentions || [] }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to add comment');
    }

    return data;
  },

  searchMentionableUsers: async (issueKey: string, query: string): Promise<MentionUser[]> => {
    const response = await fetchWithAuth(
      `issues/${encodeURIComponent(issueKey)}/mentionable-users?query=${encodeURIComponent(query)}`
    );
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to search mentionable users');
    }

    return data.data || [];
  },

  deleteComment: async (issueKey: string, commentId: string, scope: CommentScope = 'user') => {
    const response = await fetchWithAuth(
      `${commentsPath(issueKey, scope)}/${encodeURIComponent(commentId)}`,
      { method: 'DELETE' }
    );

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to delete comment');
    }

    return data;
  },
};
